import React from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styled from 'styled-components';

import {
    Sizes,
    Checkmark,
    noticeError,
    TooltipAnchor,
    TOOLTIP_END_ALIGNMENT,
    TOOLTIP_BOTTOM_LOCATION,
    DefaultIntlFormatValues,
} from '@ratehub/base-ui';

import APPROVAL_LIKELIHOOD from '../definitions/ApprovalLikelihood';
import APPROVAL_LIKELIHOOD_COLOURS from '../definitions/ApprovalLikelihoodColours';


function ApprovalLikelihoodPill({
    approvalLikelihood,
    isCompact = false,
    showEligibilityReminder,
    className,
}) {
    if (!Object.values(APPROVAL_LIKELIHOOD).includes(approvalLikelihood)) {
        noticeError(new RangeError(`Unexpected approvalLikelihood value: “${approvalLikelihood}”`), {
            expectedValues: `Expected values are: [ “${Object.values(APPROVAL_LIKELIHOOD).map(al => al).join('”, “')}” ]`,
        });

        return null;
    }


    const dynamicTooltip = (
        <TooltipAnchor
            className="tooltip rh-ml-0_25 rh-text-align-left"
            // buttonSize of 1.25rem seems right in this context, but it is not supported
            // this is 1rem; XS is 1.5rem and looks unwieldy
            buttonSize={Sizes.ICONS['2XS']}
            message={(
                <>
                    <If condition={approvalLikelihood === APPROVAL_LIKELIHOOD.PREQUALIFIED}>
                        <FormattedMessage
                            id="edb.cc.prequalified.tooltip"
                            defaultMessage="Based on your TransUnion credit report, you are prequalified. You can apply confidently, but approval is not guaranteed."
                        />
                        <If condition={showEligibilityReminder}>
                            <br />
                            <br />
                        </If>
                    </If>
                    <If condition={showEligibilityReminder}>
                        <FormattedMessage
                            id="edb.cc.reassess.tooltip"
                            defaultMessage="This is based on the last eligibility check with Ratehub that you completed. To re{nbhy}assess your approval odds, click “check{nbsp}eligibility”."
                            values={DefaultIntlFormatValues}
                        />
                    </If>
                </>
            )}
            nubbinAlignment={TOOLTIP_END_ALIGNMENT}
            tooltipLocation={TOOLTIP_BOTTOM_LOCATION}

            tooltipWidth="21em"     // max width for in iPhone SE based on tooltip position
            data-test-name="edb.cc.approval-likelihood.tooltip"
        />
    );

    const isLargerText = !isCompact || approvalLikelihood === APPROVAL_LIKELIHOOD.PREQUALIFIED;

    return (
        <Container
            className={classNames(className,
                'rh-display-flex rh-text-align-center rh-justify-content-center',
                'rh-mb-0_5 rh-py-0_5 rh-px-0_25 rh-border-radius-8px',
                {
                    'rh-text-xs': isLargerText,
                    'rh-text-2xs': !isLargerText,
                }
            )}
            approvalLikelihood={approvalLikelihood}
            data-test-name="approval-likelihood-pill"
        >
            <span className="rh-display-inline-flex rh-align-items-center">
                <FormattedMessage
                    id="cardfinder.bnb.approval-likelihood"
                    defaultMessage={`<strong>
                        {approvalLikelihood, select,
                            prequalified {<prequalified>You’re prequalified!</prequalified>}
                            other {Approval odds: <bolder>{
                                approvalLikelihood, select,
                                    excellent {EXCELLENT}
                                    very_good {VERY GOOD}
                                    good {GOOD}
                                    fair {FAIR}
                                    poor {POOR}
                                    other {N/A}
                                }</bolder>
                            }
                        }
                        </strong>
                        {showEligibilityReminder, select,
                            true {<tooltip></tooltip>}
                            other {}
                        }
                    `}
                    values={{
                        ...DefaultIntlFormatValues,
                        // workaround because the ICU Message Format syntax does not support "-" within selectors
                        approvalLikelihood: approvalLikelihood.replace('-', '_'),
                        showEligibilityReminder: showEligibilityReminder
                            || approvalLikelihood === APPROVAL_LIKELIHOOD.PREQUALIFIED,
                        // eslint-disable-next-line react/jsx-one-expression-per-line
                        bolder: str => <span className="weight-bold">{str}</span>,

                        // we won’t always have a tooltip, but there are two flavours of it
                        tooltip: () => dynamicTooltip,
                        // prequalified is different: different messaging and includes a checkmark
                        prequalified: str => (
                            // the <Checkmark /> makes the overall height of this pill slightly taller than the others
                            <span className="rh-display-inline-flex rh-align-items-center">
                                <Checkmark
                                    className="rh-mr-0_25"
                                    stroke={APPROVAL_LIKELIHOOD_COLOURS.prequalified.stroke}
                                    width="1.5em"
                                    height="1.5em"
                                />
                                <span className="rh-mr-0_25">
                                    {str}
                                </span>
                            </span>
                        ),
                    }}
                />
            </span>
        </Container>
    );
}

ApprovalLikelihoodPill.propTypes = {
    approvalLikelihood: PropTypes.oneOf(Object.values(APPROVAL_LIKELIHOOD)),
    isCompact: PropTypes.bool,
    showEligibilityReminder: PropTypes.bool,
    className: PropTypes.string,
};

const Container = styled.div`
    ${({ approvalLikelihood }) => `
        // cannot use getBGColourClassName() on Container since one of the colours needs rgba()
        background-color: ${APPROVAL_LIKELIHOOD_COLOURS[approvalLikelihood]?.backgroundColour};
    `};

    .tooltip {
        // Tooltip Anchor uses QuestionMarkButton which has a margin-bottom of -2px which 
        // we’re not a fan of here; this helps to negate that; but there’s also a difference
        // on either side of when our rem switches from 16px to 14px
        top: -0.0625rem;
    }
`;


export default ApprovalLikelihoodPill;
